<template>
  <!-- eslint-disable -->
  <div class="caseStudy view">
    <Header></Header>
    <div class="caseStudy__container layoutGrid">
      <h1 class="caseStudy__title h1">The Fish Place</h1>
      <p class="caseStudy__paragraph p">
        High rates of overfishing of specific species are being perpetuated by a purchasing and distribution process that is opaque and complex. The distance between production and consumption has resulted in a demand-driven market that values species based on familiarity and not taste or sustainability.
      </p>
      <p class="caseStudy__em p p__em">
        How might we create a balance between supply and demand in the fishing industry?
      </p>
      <h2 class="caseStudy__subtitle h2">
        Our Solution
      </h2>
      <p class="caseStudy__paragraph p">
        A simple tool that offers transparent data collection and sharing, directly connecting fisherpeople, distributors, and chefs for a streamlined buying and selling process. A multi-media solution enhances usability at every stage of the system, and provides a medium for which underutilized seafood can now enter the marketplace.
      </p>
      <img
        src="@/assets/fish/fs_01.jpg"
        alt="Storyboard showing The Fish Place concept"
        class="caseStudy__image"
      />
      <h2 class="caseStudy__subtitle h2">
        Model of Change
      </h2>
      <p class="caseStudy__paragraph p">
        Our solution aims to inform consumer habits by leveraging an existing desire for sustainable practices within leading restaurants. By engaging these high profile stakeholders and helping them transition to more sustainable methods we hope to set an example for the entire industry. In doing so we intend to create a trickle down to industrial and lower cost restaurants by making more sustainable seafood species “trendy” for consumers.
      </p>
      <h2 class="caseStudy__subtitle h2">
        Value Proposition By Stakeholder
      </h2>
      <img
        src="@/assets/fish/fs_03.jpg"
        alt="Image showing each stakeholder from the ecosystem"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        <span class="p__b">Fisherpeople:</span> simplified data shared to marketplace; ability to sell undervalued seafood.
      </p>
      <p class="caseStudy__paragraph p">
        <span class="p__b">Distributors:</span> access to transparent seafood data; enhanced ability to share data with buyers.
      </p>
      <p class="caseStudy__paragraph p">
        <span class="p__b">Buyers/Chefs:</span>  ability to purchase undervalued seafood; better understanding of seafood pathway; greater communication with consumers on sustainable seafood.
      </p>
      <p class="caseStudy__paragraph p">
        <span class="p__b">Consumers:</span> better understanding of undervalued and sustainable seafood; altered consuming habits.
      </p>
      <h2 class="caseStudy__subtitle h2">
        Initial Exploration
      </h2>
      <p class="caseStudy__paragraph p">
        We started our exploration of the fish ecosystem by speaking with several experts in different parts of a fish’s journey.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Cheryl Dahle Interview
      </h3>
      <img
        src="@/assets/fish/fs_04.png"
        alt="Headshot of Cheryl Dahle"
        class="caseStudy__image--thin caseStudy__image--lowBottom caseStudy__image--lowTop pad__bottom"
      />
      <p class="caseStudy__paragraph p">
        Cheryl Dahle is a journalist and an entrepreneur who founded future of fish as a non-profit innovation hub that supports research, design, and businesses services to organizations and individuals accelerating sustainability and traceability in seafood supply chains.
      </p>
      <p class="caseStudy__paragraph p">
        Through research done by future of fish, the key findings were that consumers find ‘storied fish’ appealing, but have trouble locating it when purchasing seafood. During further exploration, the definition of ‘storied fish’ was identified, along with market testing for this concept. A detailed report released by Future of Fish provided us with these and other underlying conclusions that helped shape our understanding of the problem space.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Barton Seaver Interview
      </h3>
      <img
        src="@/assets/fish/fs_05.png"
        alt="Headshot of Barton Seaver"
        class="caseStudy__image--thin caseStudy__image--lowBottom caseStudy__image--lowTop pad__bottom"
      />
      <p class="caseStudy__em p p__em">
        “Whether we know it or not, we are largely eating ‘mystery’ fish.”
      </p>
      <p class="caseStudy__paragraph p">
        As a firm believer in the ties between human and ocean health, Barton spent many years as a renowned executive chef and restaurant owner before transitioning towards local and international initiatives.
      </p>
      <p class="caseStudy__paragraph p">
        Throughout his restaurant career he witnessed many examples of issues within the system, but none so memorable as the time that he was delivered bait fish, with the reason behind it being that there was no other catch made that day. As a way to showcase the opportunities that our oceans present, Barton served the undervalued fish, while never missing the chance to emphasize to his customers exactly what the story behind it was.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Key Takeaways
      </h3>
      <h4 class="caseStudy__paragraph h4">
        The fishing industry is complex
      </h4>
      <p class="caseStudy__paragraph p">
        With touchpoints and turns and a lack of communication, the story of fish is being lost and has created an ecosystem that is opaque and difficult to maneuver.
      </p>
      <h4 class="caseStudy__paragraph h4">
        Undervalued seafood deserves a seat at the table
      </h4>
      <p class="caseStudy__paragraph p">
        By not only giving stories to the mystery fish that we already consume but also to the ones that we could consume, undervalued seafood might serve to recalibrate our ocean stocks by removing pressure from our overfished species, and ultimately restoring our complicated relationship with the oceans.
      </p>
      <h2 class="caseStudy__subtitle h2">
        Define
      </h2>
      <p class="caseStudy__paragraph p">
        After learning about the complicated interactions that exist within the industry, our team began iterations on our stakeholder ecosystem.
      </p>
      <img
        src="@/assets/fish/fs_06.jpg"
        alt="Early draft of a stakeholder map/supply chain"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        While we found that identifying stakeholders was a fairly straightforward task, understanding how each interacts with the other was more difficult, especially when certain aspects of interactions occur outside of the US, where we have even less data to draw from.
      </p>
      <img
        src="@/assets/fish/fs_07.jpg"
        alt="draft of a stakeholder map/supply chain"
        class="caseStudy__image--left caseStudy__image--padTop"
      />
      <img
        src="@/assets/fish/fs_08.jpg"
        alt="draft of a stakeholder map/supply chain"
        class="caseStudy__image--right caseStudy__image--padTop"
      />
      <img
        src="@/assets/fish/fs_09.jpg"
        alt="draft of a stakeholder map/supply chain"
        class="caseStudy__image--left caseStudy__image--padBottom"
      />
      <img
        src="@/assets/fish/fs_10.jpg"
        alt="draft of a stakeholder map/supply chain"
        class="caseStudy__image--right caseStudy__image--padBottom"
      />
      <p class="caseStudy__paragraph p">
        Through multiple iterations and substantial remodeling, our team was able to create a stakeholder process map that we felt identified the key characteristics of the ecosystem. It also served to establish where and with what stakeholders we wanted our intervention to exist.
      </p>
      <img
        src="@/assets/fish/fs_11.jpg"
        alt="Final stakeholder map/supply chain"
        class="caseStudy__image"
      />
      <h2 class="caseStudy__subtitle h2">
        Research
      </h2>
      <p class="caseStudy__paragraph p">
        After our interviews with Cheryl and Barton we began reachout out to people throughout the fish distribution chain and conducted a series of interviews. We focused on how fish made it through that distribution chain and what the pain points were in the existing system.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Interview Insights
      </h3>
      <p class="caseStudy__em p">
        “People don’t like different. They want the fish they have always eaten.”
      </p>
      <p class="caseStudy__em p">
        “You come in in the morning, you open your email up, and you’ll have 50-60 emails from different suppliers.”
      </p>
      <p class="caseStudy__em p">
        “Availability is challenging; thinking something should be available and it’s not. Two weeks go by and something happens where that fish isn’t available.”
      </p>
      <p class="caseStudy__em p">
        “You’re seeing fish that aren’t migrating back into the same area as they used to, and the consumer doesn’t understand how it all works and wants the fish they want.” 
      </p>
      <h3 class="caseStudy__smallheader h3">
        Key Takeaways
      </h3>
      <h4 class="caseStudy__paragraph h4">
        Disconnected Stakeholder System
      </h4>
      <p class="caseStudy__paragraph p">
        The links between fisherpeople, distributors, and chefs or buyers is disconnected and often frustrating, allowing consumer habits to be the governing force of demand that drives supply.
      </p>
      <h4 class="caseStudy__paragraph h4">
        Unreliable and Changing Oceans
      </h4>
      <p class="caseStudy__paragraph p">
        Popular fish are being drastically overfished, while underutilized seafood is almost completely left out of the market. Additionally, changing ocean patterns and ecosystems are further intensifying these effects, creating an unreliable market that cannot be sustained.
      </p>
      <h4 class="caseStudy__paragraph h4">
        Demand Driven Ecosystem
      </h4>
      <p class="caseStudy__paragraph p">
        Most chefs are frustrated with the demand-driven process of ordering the fish that are expected by their customers, but not necessarily available.
      </p>
      <h4 class="caseStudy__paragraph h4">
        Sustainability is Already Valued
      </h4>
      <p class="caseStudy__paragraph p">
        Chefs understand the importance of sustainable seafood and make efforts to support it, but face challenges with accessing data on alternative species.
      </p>
      <h2 class="caseStudy__subtitle h2">
        Ideate
      </h2>
      <p class="caseStudy__paragraph p">
        As our team ideated around everything we had learned from domain experts and field research, three possible solutions became apparent.
      </p>
      <img
        src="@/assets/fish/fs_12.jpg"
        alt="Image highlighting the three possible design solutions we identified"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        With the ‘story’ of fish being lost, we presented the idea of labeled traceability. We then looked more at the chefs, who required a communications tool to market underutilized seafood to their customers. Finally, we identified the need for a marketplace that would directly connect our key stakeholders and bring data forward.
      </p>
      <img
        src="@/assets/fish/fs_13.jpg"
        alt="Storyboard showing the labeled traceability concept"
        class="caseStudy__image--left caseStudy__image--padTop"
      />
      <img
        src="@/assets/fish/fs_15.jpg"
        alt="Storyboard showing the marketplace concept"
        class="caseStudy__image--right caseStudy__image--padTop"
      />
      <img
        src="@/assets/fish/fs_14.jpg"
        alt="Storyboard showing the communications tool concept"
        class="caseStudy__image caseStudy__image--lowTop"
      />
      <h2 class="caseStudy__subtitle h2">
        Prototype
      </h2>
      <h3 class="caseStudy__smallheader h3">
        The App for FisherPeople
      </h3>
      <p class="caseStudy__paragraph p">
        The app will allow fisherpeople to quickly and easily share data on what they catch. It will also empower them with the ability to market seafood that is typically undervalued but available. This will boost their sales, enhance ocean sustainability, and ultimately increase the resiliency of their business.
      </p>
      <div class="video-container caseStudy__video--thin">
        <iframe
          src="https://player.vimeo.com/video/340793109"
          frameborder="0"
          allow="autoplay; fullscreen"
          class="video-video"
          allowfullscreen
        />
      </div>
      <h3 class="caseStudy__smallheader h3">
        The Distributor Toolkit
      </h3>
      <p class="caseStudy__paragraph p">
        As a distributor, the ability to receive and share accurate and transparent data is paramount. With automated emails that can be shared almost immediately, gone will be the days of confusing spreadsheets and miscommunication.
      </p>
      <img
        src="@/assets/fish/fs_16.jpg"
        alt="Image of the distributor email building toolkit"
        class="caseStudy__image"
      />
      <p class="caseStudy__paragraph p">
        Meanwhile Chefs & Buyers have access to a clear, up to date, and easy to use marketplace to buy fish from any distributor.
      </p>
      <div class="video-container caseStudy__video">
        <iframe
          src="https://player.vimeo.com/video/340793428"
          frameborder="0"
          allow="autoplay; fullscreen"
          class="video-video"
          allowfullscreen
        />
      </div>
      <h3 class="caseStudy__smallheader h3">
        The Chef/Buyer Marketplace & Marketing Tool
      </h3>
      <p class="caseStudy__paragraph p">
        With data no longer focused solely on a few species of fish, chefs and buyers will have access to new seafood markets. Additionally, with the marketing tool specifically designed to enhance their communication with customers, they can elevate the story of fish and extol the benefits of consuming sustainably.
      </p>
      <img
        src="@/assets/fish/fs_17.jpg"
        alt="Concept of the marketing toolkit"
        class="caseStudy__image"
      />
      <h2 class="caseStudy__subtitle h2">
        Reflect
      </h2>
      <h3 class="caseStudy__smallheader h3">
        Business Model
      </h3>
      <p class="caseStudy__paragraph p">
        While the hope is for altered consumer habits to emerge, the nature of this innovation means it will function best under a ‘trickle down’ approach. This will target higher-end restaurants initially, before making undervalued seafood a ‘trendy’ option for every-day consumers.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Partners in Growth
      </h3>
      <p class="caseStudy__paragraph p">
        As a way to incentivize use and maximize exposure to stakeholders, we imagine that the fish place would be best implemented if funded by distributors themselves, who can then ensure its uptake via existing relationships with fisherpeople and chefs.
      </p>
      <h3 class="caseStudy__smallheader h3">
        Elevating The Story of Fish
      </h3>
      <p class="caseStudy__paragraph p">
        If we were to continue to iterate on our design, we would focus on more reporting opportunities to bring the fisherperson and their experiences of their catch forward directly to the consumer, as we learned it is storytelling that has the power to influence consumer behavior.o
        
      </p>
    </div>
  </div>
</template>

<script>
import Header from "@/components/header.vue";
export default {
  components: {
    Header
  }
};
</script>
